// Tasks.js
import React, { useState, useEffect, useCallback } from "react";
import {
  message,
  Button,
  Flex,
  Tabs,
  Select,
  Tooltip,
  Switch,
  Typography,
  DatePicker,
  Modal,
} from "antd";
import orderService from "../../services/OrderLists";
import OrderListTable from "./OrderListTable";

import { CSSTransition } from "react-transition-group";
import { FilterOutlined, InfoCircleOutlined, DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "./tasks.css";
import SearchFilters from "./SearchFilters";
import MobileViewComponent from "./MobileViewComponent";
import usePermissions from "../../hooks/usePermissions";
import OrderFormParent from "./OrderFormParent";
import { useMediaQuery } from "react-responsive";

import { debounce } from "lodash";
import { useMemo } from "react";
import hierarchyService from "../../services/hierarchyService";
import orderTagService from "../../services/orderTagService";

const { Option } = Select;
const { Text } = Typography;
const MAX_CONCURRENT_REQUESTS = 4; // Limit for concurrent requests
const requestQueue = [];
let activeRequests = 0;

// Request Queue Manager
const processQueue = async () => {
  if (requestQueue.length === 0 || activeRequests >= MAX_CONCURRENT_REQUESTS) {
    return;
  }

  const { requestFn, resolve, reject } = requestQueue.shift();
  activeRequests++;

  try {
    const result = await requestFn();
    resolve(result);
  } catch (error) {
    reject(error);
  } finally {
    activeRequests--;
    processQueue();
  }
};

// Wrapper for API Calls
const enqueueRequest = (requestFn) =>
  new Promise((resolve, reject) => {
    requestQueue.push({ requestFn, resolve, reject });
    processQueue();
  });

const Tasks = () => {
  const { TabPane } = Tabs;
  const { hasPermission } = usePermissions();
  const [subscribers, setSubscribers] = useState([]);
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [selectedOption, setSelectedOption] = useState("my-orders");
  const [filtersList, setFiltersList] = useState([]);
  const [fetchingStatusList, setFetchingStatusList] = useState(false);
  const [toggleAssignedOrders, setToggleAssignedOrders] = useState(false);
  const [hierarchyData, setHierarchyData] = useState([]);
  const [selectedHierarchy, setSelectedHierarchy] = useState([]);
  const [parentDetail, setParentDetail] = useState({ user: null, uid: null });
  const [selectedSortOrder, setSelectedSortOrder] = useState({
    order: "",
    sort: "",
  });
  const [treeData, setTreeData] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [taggedTaskKeys, setTaggedTaskKeys] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [myTeamloading, setMyTeamLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  const [statusList, setStatusList] = useState({}); // New state for status list
  const [ownerShipList, setOwnerShipList] = useState([]);
  const [situationsList, setSituationsList] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [urgencyFilter, setUrgencyFilter] = useState(""); // Default filter to "Deadline > 1 Day"
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
  });

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalKey, setModalKey] = useState(0);

  // Download CSV state — date range picker with 1-week max limit
  const [isDownloadModalVisible, setIsDownloadModalVisible] = useState(false);
  const [downloadDateRange, setDownloadDateRange] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleToggleAssignedOrders = () => {
    setToggleAssignedOrders(!toggleAssignedOrders);
  };
  const refreshTagData = useCallback(() => {
    orderTagService.getTags().then((res) => {
      if (res?.success) setAllTags(res.data || []);
    });
    orderTagService.getTaggedTaskKeys().then((res) => {
      if (res?.success) setTaggedTaskKeys(new Set(res.data || []));
    });
  }, []);

  useEffect(() => {
    refreshTagData();
  }, [refreshTagData]);

  // Refresh allTags when sidebar creates/edits/deletes a tag
  useEffect(() => {
    const handler = () => refreshTagData();
    window.addEventListener("tag-list-changed", handler);
    return () => window.removeEventListener("tag-list-changed", handler);
  }, [refreshTagData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setMyTeamLoading(true);
        const result = await hierarchyService.fetchBdmAnalyticsHierarchy();

        if (result.success) {
          const data = result.data.data;
          // Filter out parent UID from hierarchyData if present
          const filteredHierarchy = data.hierarchy.filter((item) => {
            // Allow self-reference ONLY if it has children
            if (item.username === data.username) {
              return item.hierarchy && item.hierarchy.length > 0;
            }
            return true;
          });
          setHierarchyData(filteredHierarchy);
          setParentDetail({ uid: data.uid, username: data.username }); // Save UID and user
          setSelectedHierarchy([data.uid]);
          // Build treeData for TreeSelect (add parent as selectable node at top)
          // Helper: Recursively check if a uid exists in hierarchy
          function uidInHierarchy(hierarchy, uid) {
            for (const item of hierarchy) {
              if (item.uid === uid) return true;
              if (item.hierarchy && uidInHierarchy(item.hierarchy, uid))
                return true;
            }
            return false;
          }

          // Helper: Recursively add (You) to the node if uid matches data.uid

          function convertHierarchyToTreeData(hierarchy) {
            return hierarchy.map((item) => ({
              title:
                item.is_active === false ? (
                  <span>
                    {item.user}{" "}
                    <span style={{ fontStyle: "italic", color: "#888" }}>
                      {" "}
                      (inactive)
                    </span>
                  </span>
                ) : (
                  item.user
                ),
              value: item.username,
              key: item.username,
              children:
                item.hierarchy && item.hierarchy.length > 0
                  ? convertHierarchyToTreeData(item.hierarchy)
                  : undefined,
            }));
          }

          // Helper: Remove and return node with uid from hierarchy
          function extractUserNode(hierarchy, uid) {
            let foundNode = null;
            function recurse(nodes) {
              return nodes.filter((item) => {
                if (item.uid === uid) {
                  foundNode = { ...item };
                  return false;
                }
                if (item.hierarchy && item.hierarchy.length > 0) {
                  item.hierarchy = recurse(item.hierarchy);
                }
                return true;
              });
            }
            const newHierarchy = recurse([...hierarchy]);
            return { foundNode, newHierarchy };
          }

          let tree = [];
          tree.push({ title: "Total", value: "Total", key: "Total" });
          if (!uidInHierarchy(filteredHierarchy, data.uid)) {
            tree.push({
              title: `${data.user} (You)`,
              value: data.username,
              key: data.username,
              children: [],
            });
            tree = tree.concat(convertHierarchyToTreeData(filteredHierarchy));
          } else {
            // Extract user node, move to top, append (You)
            const { foundNode, newHierarchy } = extractUserNode(
              filteredHierarchy,
              data.username,
            );
            if (foundNode) {
              tree.push({
                title: `${foundNode.user} (You)`,
                value: foundNode.username,
                key: foundNode.username,
                children:
                  foundNode.hierarchy && foundNode.hierarchy.length > 0
                    ? convertHierarchyToTreeData(foundNode.hierarchy)
                    : undefined,
              });
              tree = tree.concat(convertHierarchyToTreeData(newHierarchy));
            } else {
              tree = tree.concat(convertHierarchyToTreeData(filteredHierarchy));
            }
          }
          setTreeData(tree);
          // Automatically fetch analytics data for the default user
          // fetchAnalyticsData(
          //   null, // No date range
          //   null, // No date range
          //   dayjs().month() + 1, // Current month
          //   dayjs().year(), // Current year
          //   [data.uid] // Default user UID
          // );
          setMyTeamLoading(false);
        }
      } catch (error) {
        setMyTeamLoading(false);
        console.error(error);
      }
    };

    fetchData();
  }, []);

  // TreeSelect change handler
  // Only store the checked values as selectedHierarchy. Don't add ancestors.
  // With treeCheckStrictly, checkedNodes is an array of checked nodes: [{label, value, halfChecked}]
  const handleTreeSelectChange = (checkedNodes) => {
    function getAllDescendantUids(node) {
      let uids = [];
      if (node.value !== "Total") uids.push(node.value);
      if (node.children) {
        node.children.forEach((child) => {
          uids = uids.concat(getAllDescendantUids(child));
        });
      }
      return uids;
    }

    function getAllUidsFromTree(tree) {
      let uids = [];
      for (const node of tree) {
        if (node.value !== "Total") {
          uids.push(node.value);
        }
        if (node.children && node.children.length > 0) {
          uids = uids.concat(getAllUidsFromTree(node.children));
        }
      }
      return uids;
    }

    let values = checkedNodes.map((node) => node.value);

    // Handle 'Total' select/deselect logic
    if (values.includes("Total")) {
      const allUids = getAllUidsFromTree(treeData);
      // If all are already selected, deselect all
      const allSelected =
        allUids.length > 0 && allUids.every((uid) => values.includes(uid));
      if (allSelected) {
        setSelectedHierarchy([]);
      } else {
        setSelectedHierarchy(allUids);
      }
      return;
    }

    setSelectedHierarchy(values);
  };
  const showModal = () => {
    setIsModalVisible(true);
    setModalKey((prev) => prev + 1);
  };

  const closeModal = () => {
    setIsModalVisible(false);
  };
  const handleTabChange = (key) => setUrgencyFilter(key);
  // Debounced version of the loadOrders function
  const debouncedLoadOrders = useMemo(
    () =>
      debounce(async (urgencyLevel, page = 1, pageSize = 10) => {
        setLoading(true);

        // console.log("filters_insideFun",filters)

        try {
          const result = await enqueueRequest(() =>
            orderService.fetchOrders(
              urgencyLevel || null,
              {
                ...filters,
                order: selectedSortOrder.order,
                sort: selectedSortOrder.sort,
              },

              page,
              pageSize,
              selectedOption,
              toggleAssignedOrders,
              // filters
            ),
          );

          if (result.success) {
            setFiltersList(result?.data?.data?.filters);
            setOrders(result.data.data.results);
            setPagination({
              ...pagination,
              total: result?.data?.data?.count || 0,
              current: page,
              pageSize,
            });
          } else {
            message.error("Failed to fetch orders");
          }
        } catch (error) {
          console.error("Error fetching orders:", error);
        } finally {
          setLoading(false);
        }
      }, 500), // Debounce delay
    [selectedOption, pagination, filters, toggleAssignedOrders],
  );

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };
  const handleClearSearch = async () => {
    setFilters({});

    // await loadOrders(urgencyFilter, 1);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const order =
      sorter.order === "ascend"
        ? "asc"
        : sorter.order === "descend"
          ? "desc"
          : "";
    setSelectedSortOrder({ order: sorter.field, sort: order });
    setPagination((prev) => ({
      ...prev,
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
    // loadOrders(urgencyFilter, pagination.current, pagination.pageSize);
    debouncedLoadOrders(
      urgencyFilter,
      pagination.current,
      pagination.pageSize,
      filters,
    );
  };

  // console.log("filters_outtt",filters)

  const fetchStatusList = async () => {
    try {
      setFetchingStatusList(true);
      const response = await orderService.getStatusList();

      if (response) {
        setFetchingStatusList(false);
        const newStatusList = {};
        for (const [key, value] of Object.entries(response.data.data)) {
          newStatusList[value] = parseInt(key);
        }
        setStatusList(newStatusList);
      } else {
        setFetchingStatusList(false);

        console.error("Failed to fetch status list or invalid response format");
        console.error("Response:", response);
      }
    } catch (error) {
      setFetchingStatusList(false);
      console.error("Error fetching status list:", error);
    }
  };

  // const fetchBDMData = async () => {
  //   setFetchingStatusList(true);
  //     try {
  //       const result = await hierarchyService.fetchBdmAnalyticsHierarchy();
  //       if (result.success) {
  //         const data = result.data.data;
  //         setHierarchyData(data.hierarchy);
  //         setParentDetail({ uid: data.uid, user: data.user });
  //          setSelectedHierarchy([data.uid]);
  //          setFetchingStatusList(false);
  //       }
  //     } catch (error) {
  //       setFetchingStatusList(false);
  //       console.error(error);
  //     }
  //  };

  const fetchOwnershipData = async () => {
    setFetchingStatusList(true);
    try {
      const result = await hierarchyService.fetchOwnershipHierarchy();
      if (result.success) {
        const uniqueOwnershipList = Array.from(
          new Map(result.data.data.map((item) => [item.email, item])).values(),
        );

        setOwnerShipList(uniqueOwnershipList);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setFetchingStatusList(false);
    }
  };

  const fetchDropdownList = async () => {
    try {
      const dropdownList = await orderService.getSubscribersDropDown();

      setSubscribers(dropdownList?.data?.data || []);
    } catch (error) {
      console.error("Error fetching dropdown list:", error);
    }
  };

  const fetchSituationsList = async () => {
    try {
      const result = await orderService.getSituationDropdown();
      setSituationsList(result?.data || []);
    } catch (error) {
      console.error("Error fetching situations list:", error);
    }
  };

  // useEffect(()=>{
  //   const yesMap  = filtersList?.includes("sales_team")
  //   // console.log("yesMap",yesMap)
  //   if(yesMap){
  //     fetchBDMData()
  //   }
  // },[filtersList])

  useEffect(() => {
    return () => {
      debouncedLoadOrders.cancel();
    };
  }, [debouncedLoadOrders]);
  useEffect(() => {
    fetchStatusList();
    fetchDropdownList();
    fetchOwnershipData();
    fetchSituationsList();
    debouncedLoadOrders(urgencyFilter, pagination.current, pagination.pageSize);
  }, [
    urgencyFilter,
    pagination.current,
    pagination.pageSize,
    filters,
    selectedSortOrder,
    selectedOption,
    toggleAssignedOrders,
  ]);

  const handleSearchClick = (newFilters) => {
    setFilters(newFilters);
    setPagination({ ...pagination, current: 1 });
  };
  const tabConfig = [
    { key: "all", label: "All Orders", color: "blue", bgColor: "grey" },
    { key: "red", label: "Deadline Missed", color: "red", bgColor: "#FFCCCB" },
    {
      key: "orange",
      label: "Deadline > 1 Day",
      color: "orange",
      bgColor: "#FFE5B4",
    },
    {
      key: "yellow",
      label: "Deadline in 2-4 Days",
      color: "#FACA15",
      bgColor: "#FFF9C4",
    },
    {
      key: "green",
      label: "Deadline > 4 Days",
      color: "green",
      bgColor: "#C8E6C9",
    },
  ];

  const handleChange = (value) => {
    setSelectedOption(value);
  };

  /**
   * handleDownloadCSV
   *
   * Opens a modal for date range selection, then downloads order data as CSV.
   * Backend enforces a maximum of 7 days. If no dates are selected,
   * the backend defaults to the last 7 days of data.
   */
  const handleDownloadCSV = async () => {
    setIsDownloading(true);
    try {
      // Build download filters from current active filters + date range
      const downloadFilters = { ...filters, urgency_level: selectedOption };

      // Add CSV date range if user selected one
      // Uses csv_start_date/csv_end_date to avoid collision with the
      // existing start_date filter (which filters by Task.start_date field)
      if (downloadDateRange && downloadDateRange[0] && downloadDateRange[1]) {
        downloadFilters.csv_start_date = downloadDateRange[0].format("YYYY-MM-DD");
        downloadFilters.csv_end_date = downloadDateRange[1].format("YYYY-MM-DD");
      }

      const response = await orderService.downloadOrderListCSV(
        pagination.current,
        pagination.pageSize,
        downloadFilters,
      );

      if (response?.success) {
        message.success("CSV downloaded successfully!");
        setIsDownloadModalVisible(false);
        setDownloadDateRange(null);
      } else {
        message.error("Failed to download CSV");
      }
    } catch (error) {
      console.error("Error downloading CSV:", error);
      message.error("An error occurred while downloading CSV");
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * Validates the selected date range — max 7 days allowed.
   * Disables dates that are more than 7 days from the first selected date,
   * and prevents selecting future dates.
   */
  const disabledDate = (current, { from }) => {
    // Don't allow future dates
    if (current && current > dayjs().endOf("day")) {
      return true;
    }
    // If a start date is selected, restrict end date to within 7 days
    if (from) {
      const maxEnd = dayjs(from).add(7, "day");
      const minStart = dayjs(from).subtract(7, "day");
      return current < minStart || current > maxEnd;
    }
    return false;
  };

  useEffect(() => {
    if (selectedOption === "team-orders" || selectedOption === "my-orders") {
      debouncedLoadOrders(selectedOption);
    }
  }, [selectedOption, filters]);

  const salesTeamOptions = [
    {
      value: parentDetail.username,
      label: parentDetail.user ? `${parentDetail.user} (You)` : "(You)",
    },
    ...hierarchyData.map((item) => ({
      value: item.username,
      label: item.user,
    })),
  ];

  // const ownershipOptions = ownerShipList.map((item) => ({
  //   value: item.email,
  //   label: item.user,
  // }))

  return (
    <div className="mainParent">
      {/* Filters Section */}
      {showFilters && (
        <CSSTransition
          in={showFilters}
          timeout={300}
          classNames="filter"
          unmountOnExit
        >
          <SearchFilters
            filtersList={filtersList}
            onSearch={handleSearchClick}
            loadOrders={handleClearSearch}
            pagination={pagination}
            statusList={statusList}
            fetchingStatusList={fetchingStatusList}
            subscribers={subscribers}
            setSubscribers={setSubscribers}
            ownerShipList={ownerShipList}
            setOwnerShipList={setOwnerShipList}
            hierarchyData={hierarchyData} // still fine to pass
            parentDetail={parentDetail} // in case needed
            salesTeamOptions={salesTeamOptions} // ✅ pass this new prop
            treeData={treeData}
            situationsList={situationsList}

            // ownershipOptions={ownershipOptions}
          />
        </CSSTransition>
      )}

      <Flex gap={4}>
        <Select
          placeholder="Select an action"
          style={{ width: "100%" }}
          disabled={loading}
          value={selectedOption}
          onChange={handleChange}
        >
          <Option value="my-orders">My Orders</Option>
          {hasPermission("can_view_team_member_orders") && (
            <Option value="team-orders">Team Orders</Option>
          )}
        </Select>
        {hasPermission("can_create_task") && (
          <Button
            type="primary"
            onClick={showModal}
            disabled={loading}
            style={{ background: "#001529", color: "white" }}
          >
            Add Order
          </Button>
        )}

        {/* Download CSV button — opens date range modal, max 1 week */}
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => setIsDownloadModalVisible(true)}
          disabled={loading}
          style={{ backgroundColor: "#040724" }}
        >
          Download CSV
        </Button>

        {/* Download CSV Modal — date range selection with 7-day max */}
        <Modal
          title="Download Order Data (CSV)"
          visible={isDownloadModalVisible}
          onOk={handleDownloadCSV}
          onCancel={() => {
            setIsDownloadModalVisible(false);
            setDownloadDateRange(null);
          }}
          confirmLoading={isDownloading}
          okText="Download"
        >
          <p style={{ marginBottom: 8, color: "#666" }}>
            Select a date range (maximum 7 days). If no range is selected,
            the last 7 days will be downloaded.
          </p>
          <DatePicker.RangePicker
            value={downloadDateRange}
            onChange={(dates) => setDownloadDateRange(dates)}
            disabledDate={disabledDate}
            style={{ width: "100%" }}
            format="DD-MM-YYYY"
          />
        </Modal>

        <OrderFormParent
          visible={isModalVisible}
          setVisible={setIsModalVisible}
          onClose={closeModal}
          loadOrders={debouncedLoadOrders}
          modalKey={modalKey}
        />
        <Button
          type="primary"
          icon={<FilterOutlined />}
          onClick={toggleFilters}
          style={{ background: "#040724", width: "40px", padding: "8px" }}
        />
      </Flex>

      {/* Content Section */}
      {isMobile ? (
        <div style={{ width: "100%" }}>
          <Tabs
            onChange={handleTabChange}
            defaultActiveKey="all"
            tabBarExtraContent={
              hasPermission("can_view_superuser_orders") &&
              selectedOption === "my-orders" ? (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Tooltip
                    placement="left"
                    title={`Switch to view ${toggleAssignedOrders ? "Superuser" : "Assigned"} Orders`}
                  >
                    <InfoCircleOutlined
                      style={{
                        color: "#1890ff",
                        cursor: "pointer",
                        fontSize: "20px",
                      }}
                    />
                  </Tooltip>
                  <Switch
                    value={toggleAssignedOrders}
                    checked={toggleAssignedOrders}
                    onChange={handleToggleAssignedOrders}
                    style={{
                      backgroundColor: toggleAssignedOrders
                        ? "#36c6d3"
                        : "black",
                    }}
                  />
                </div>
              ) : null
            }
          >
            {tabConfig.map((tab) => (
              <TabPane
                tab={
                  <span
                    style={{
                      color: "black",
                      padding: "8px 16px",
                      borderRadius: "8px 8px 0 0",
                      backgroundColor: tab.bgColor,
                    }}
                  >
                    {tab.label}
                  </span>
                }
                key={tab.key}
              >
                <Flex
                  vertical
                  align="flex-end"
                  gap={10}
                  style={{ width: "100%" }}
                >
                  {!loading && (
                    <p style={{ margin: 0, padding: 0 }}>
                      Showing{" "}
                      {Math.min(
                        (pagination.current - 1) * pagination.pageSize + 1,
                        pagination.total,
                      )}{" "}
                      -{" "}
                      {Math.min(
                        pagination.current * pagination.pageSize,
                        pagination.total,
                      )}{" "}
                      of {pagination.total} Orders.
                    </p>
                  )}

                  <MobileViewComponent
                    data={orders}
                    pagination={pagination}
                    loadOrders={debouncedLoadOrders}
                    setPagination={setPagination}
                    loading={loading}
                  />
                </Flex>
              </TabPane>
            ))}
          </Tabs>
        </div>
      ) : (
        <OrderListTable
          orders={orders}
          loading={loading}
          pagination={!isSearching ? pagination : false}
          // pagination={
          //   isSearching
          //     ? false
          //     : {
          //         current: currentPage,
          //         pageSize: pageSize,
          //         total: total,
          //         showSizeChanger: false,
          //       }
          // }
          onChange={handleTableChange}
          setUrgencyFilter={setUrgencyFilter}
          loadOrders={debouncedLoadOrders}
          setPagination={setPagination}
          selectedOption={selectedOption}
          toggleAssignedOrders={toggleAssignedOrders}
          setToggleAssignedOrders={setToggleAssignedOrders}
          allTags={allTags}
          taggedTaskKeys={taggedTaskKeys}
          onTagsChanged={refreshTagData}
        />
      )}
    </div>
  );
};

export default Tasks;
